import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';
import { clipboard } from '../clipboard';
import { useFieldProps } from '../Form';
import { Tooltip, type TooltipProps } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';
import { HeadlessTextInput } from './HeadlessTextInput';

export type TextInputSize = 'small' | 'medium' | 'large';
export type TextInputFeedbackType = 'danger' | 'warning' | 'info' | 'success';

export type TextInputProps = Omit<
    React.ComponentPropsWithoutRef<'input'>,
    'size' | 'prefix' | 'onSubmit'
> & {
    /**
     * Choose a size options for the button.
     * `small` matches a `small` Button, for dense filter rows.
     * @default 'medium'
     */
    size?: TextInputSize;

    /**
     * Icon positioned ahead of the input.
     */
    leadingIcon?: IconDefinition | undefined;

    /**
     * Make the text input copyable using a button.
     * @default false, true if readOnly
     */
    copyable?: boolean;

    /**
     * Callback called when the copy button is pressed.
     */
    onCopy?: (event: {
        /**
         * The value that will be copied to clipboard.
         */
        value: string;

        /**
         * Allow to prevent the default behaviour and handle copying manually.
         */
        preventDefault: () => void;

        /**
         * Whether the default behaviour has been prevented.
         */
        isDefaultPrevented: boolean;
    }) => void;

    /**
     * Make the text input multiline.
     * @default false
     */
    multiline?: boolean;

    /**
     * Class name for the input element.
     */
    inputClassName?: string;

    /**
     * Visual style of the input
     * @default 'default'
     */
    kind?: 'default';

    /**
     * Prefix element to be rendered before the input.
     */
    prefix?: React.ReactNode;

    /**
     * Suffix element to be rendered after the input.
     */
    suffix?: React.ReactNode;

    /**
     * Free form trailing content rendered after the input, before suffix/copy controls.
     */
    trailing?: React.ReactNode;

    /**
     * Feedback message and type to display below the input.
     */
    feedback?: {
        type?: TextInputFeedbackType;
        message: React.ReactNode;
    };

    /**
     * Optional callback called when the clear button is pressed.
     * The clear button is only displayed if this callback is passed.
     */
    onClear?: () => void;

    /**
     * Callback called when pressing <Enter>. Disabled for multiline inputs.
     */
    onSubmit?: (
        value: string,
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
};

/**
 * Single-line text input. Renders a styled `<input>` (or `<textarea>` when `multiline`) wrapped
 * in a container that hosts `leadingIcon`, `prefix`, `suffix`, `trailing`, copy/clear buttons, and
 * an inline `feedback` message.
 *
 * For form wiring (label, name, validation), compose inside a `TextField` from `@gitbook/spine-react`.
 * For free-form editors, use `AutoSizeInput` (single line, auto-width) or `AutoSizeTextarea`
 * (multi line, auto-height) instead.
 */
export function TextInput({
    ref: forwardedRef,
    ...props
}: TextInputProps & { ref?: React.ForwardedRef<HTMLInputElement> }) {
    const {
        size = 'medium',
        leadingIcon,
        copyable = Boolean(props.readOnly),
        inputClassName,
        multiline = false,
        suffix,
        prefix,
        trailing,
        feedback,
        onCopy,
        onClear,
        onSubmit,
        ...inputProps
    } = props;

    const inputRef = useObjectRef(forwardedRef);
    const { onKeyDown } = inputProps;
    const { className } = useFieldProps(inputProps);

    const [value] = useControlledState(props.value, props.defaultValue ?? '');

    const canClear =
        Boolean(onClear) && Boolean(value) && !inputProps.readOnly && !inputProps.disabled;

    const feedbackTone = feedback?.type ?? null;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!event.defaultPrevented && event.key === 'Enter' && !event.shiftKey && !multiline) {
            onSubmit?.(inputRef.current?.value ?? '', event);
        }

        onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
    };

    return (
        <>
            <div
                data-spine-text-input=""
                className={clsx(
                    'relative flex w-full cursor-text items-center gap-2 overflow-hidden text-ui-base transition',
                    'rounded-md border',
                    'px-(--input-padding-x) py-(--input-padding-y)',
                    'has-[:disabled:not(button)]:cursor-not-allowed has-[:disabled:not(button)]:opacity-disabled',
                    'has-[:invalid]:border-danger-focus',
                    'has-[[aria-invalid=true]]:border-danger-focus',
                    feedbackTone
                        ? feedbackStylesClasses[feedbackTone]
                        : 'border-neutral-subtle bg-neutral-surface has-[input:focus]:focus-ring has-[textarea:focus]:focus-ring [&:not(:has(:disabled:not(button)))]:hover:bg-neutral-surface-subtle',
                    {
                        small: 'min-h-6 [--input-padding-x:0.375rem] [--input-padding-y:calc(0.125rem-1px)]',
                        medium: '[--input-padding-x:0.5rem] [--input-padding-y:calc(0.375rem-1px)]',
                        large: '[--input-padding-x:0.625rem] [--input-padding-y:calc(0.5rem-1px)]',
                    }[size],
                    className
                )}
                style={prefix || suffix ? { containerType: 'inline-size' } : undefined}
                onClick={() => inputRef.current?.focus()}
            >
                {leadingIcon
                    ? renderIcon(leadingIcon, {
                          size: size === 'small' ? 12 : 16,
                          className: 'shrink-0 text-secondary',
                      })
                    : null}

                {prefix}

                <HeadlessTextInput
                    {...(inputProps as Omit<typeof inputProps, 'onSubmit'>)}
                    ref={inputRef}
                    value={value}
                    placeholder={inputProps.placeholder ?? ''}
                    className={clsx(leadingIcon && 'pt-0.5', inputClassName)}
                    onKeyDown={handleKeyDown}
                    multiline={multiline}
                    inputClassName={
                        {
                            small: 'text-ui-small', // h-4
                            medium: 'text-ui-base', // h-5
                            large: 'text-ui-action', // h-5
                        }[size]
                    }
                />

                {trailing}

                {suffix}

                {copyable && (
                    <Button
                        kind="blank"
                        size="xsmall"
                        label="Copy"
                        iconOnly
                        leadingIcon={Icon.Copy}
                        onPress={() => {
                            let isDefaultPrevented = false;
                            const event = {
                                get isDefaultPrevented() {
                                    return isDefaultPrevented;
                                },
                                value: inputRef.current?.value ?? '',
                                preventDefault: () => {
                                    isDefaultPrevented = true;
                                },
                            };
                            onCopy?.(event);
                            if (!event.isDefaultPrevented) {
                                clipboard.writeText(event.value);
                            }
                        }}
                    />
                )}

                {canClear && (
                    <Button
                        kind="blank"
                        size="xsmall"
                        label="Clear"
                        iconOnly
                        leadingIcon={Icon.X}
                        onPress={() => {
                            onClear?.();
                            inputRef.current?.focus();
                        }}
                    />
                )}
            </div>

            {feedback?.message && (
                <span
                    className={clsx(
                        'text-ui-small',
                        feedbackTone ? feedbackMessageClasses[feedbackTone] : 'text-secondary'
                    )}
                >
                    {feedback.message}
                </span>
            )}
        </>
    );
}

export function TextInputTextPrefix(props: {
    text: string;
    tooltip?: Omit<TooltipProps, 'children'>;
}) {
    return (
        <Tooltip label={props.text} {...props.tooltip}>
            <div
                className={clsx(
                    // Temp: fallback for Firefox which doesn't support `cqw` yet
                    'max-w-[40%] supports-[width:1cqw]:max-w-[40cqw]',
                    '-my-(--input-padding-y) -ml-(--input-padding-x) flex h-[calc(1lh+var(--input-padding-y)*2)] cursor-default items-center truncate border-r bg-side px-(--input-padding-x) text-muted select-none'
                )}
            >
                <span className="truncate">{props.text}</span>
            </div>
        </Tooltip>
    );
}

export function TextInputTextSuffix(props: { text: string }) {
    return (
        <div
            className={clsx(
                // Temp: fallback for Firefox which doesn't support `cqw` yet
                'max-w-[40%] supports-[width:1cqw]:max-w-[40cqw]',
                '-my-(--input-padding-y) -mr-(--input-padding-x) flex h-[calc(1lh+var(--input-padding-y)*2)] items-center truncate border-l bg-side px-(--input-padding-x) text-muted select-none'
            )}
        >
            <span className="truncate">{props.text}</span>
        </div>
    );
}

export type SearchInputProps = Omit<TextInputProps, 'leadingIcon' | 'type'>;

export function SearchInput({
    ref,
    ...props
}: SearchInputProps & { ref?: React.ForwardedRef<HTMLInputElement> }) {
    return (
        <TextInput
            ref={ref}
            type="search"
            leadingIcon={Icon.Search}
            placeholder="Search..."
            {...props}
        />
    );
}

const feedbackStylesClasses: Record<TextInputFeedbackType, string> = {
    danger: 'border-danger-focus bg-danger-ui has-[input:focus]:shadow-focus-ring-danger-inset has-[textarea:focus]:shadow-focus-ring-danger-inset',
    warning:
        'border-warning-focus bg-warning-ui has-[input:focus]:shadow-focus-ring-warning-inset has-[textarea:focus]:shadow-focus-ring-warning-inset',
    info: 'border-info-focus bg-info-ui has-[input:focus]:focus-ring has-[textarea:focus]:focus-ring',
    success:
        'border-success-focus bg-success-ui has-[input:focus]:shadow-focus-ring-success-inset has-[textarea:focus]:shadow-focus-ring-success-inset',
};

const feedbackMessageClasses: Record<TextInputFeedbackType, string> = {
    danger: 'text-danger-low',
    warning: 'text-warning-low',
    info: 'text-info-low',
    success: 'text-success-low',
};
